/**
 * SportsGateSheet — iter-96
 *
 * Appears when an actor hits the 10-share gate on a sports game.
 * Two options:
 *   • Game Pass — $4.99 — unlimited shares for THIS game
 *   • Series Pass — $14 — unlimited shares for the whole playoff series
 *
 * Anon users see a sign-in prompt first; passes are tied to user_id so
 * they persist across devices.
 *
 * The grant endpoint is in place; this UI calls it directly for now and
 * we'll plug Stripe Checkout in front of it next.
 */
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Ticket, Trophy, Check, Sparkles } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { track } from "../lib/track";
import { toast } from "sonner";

export default function SportsGateSheet({ open, onClose, gate }) {
    const { user } = useAuth();
    const [busy, setBusy] = useState(null);

    if (!open || !gate) return null;
    const requiresLogin = !user || gate.requires_login;
    const seriesAvailable = !!gate.series_slug;

    const buyPass = async (passType) => {
        if (requiresLogin) {
            try {
                window.dispatchEvent(new CustomEvent("open-auth-modal", { detail: { source: "sports_pass_purchase" } }));
            } catch { /* ignore */ }
            return;
        }
        setBusy(passType);
        track("sports_pass_buy_attempt", { pass_type: passType, game_slug: gate.game_slug, series_slug: gate.series_slug });

        try {
            // iter-102 — purchase flow. Backend grant endpoint requires auth;
            // 401 routes to sign-in via auth modal. Once Stripe is wired
            // in production this will switch to /checkout/sports-pass.
            const { data } = await axios.post(
                `${API}/sports/event-pass/${gate.game_slug}/grant`,
                { pass_type: passType },
                { withCredentials: true },
            );
            if (data?.ok) {
                track("sports_pass_granted", { pass_type: passType });
                toast.success(
                    passType === "series"
                        ? "Series pass active. share the whole run."
                        : "Game pass active. share away.",
                    { duration: 4000 },
                );
                onClose?.();
            }
        } catch (e) {
            console.error("pass grant failed", e);
            const status = e?.response?.status;
            if (status === 401) {
                window.dispatchEvent(new CustomEvent("open-auth-modal", { detail: { source: "sports_pass_purchase" } }));
            } else {
                toast.error("couldn't activate the pass — try again");
            }
        } finally {
            setBusy(null);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
                className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/65 backdrop-blur-sm"
                data-testid="sports-gate-sheet-backdrop"
            >
                <motion.div
                    initial={{ y: 30, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 30, opacity: 0 }}
                    transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full sm:w-[440px] sm:max-w-[92vw] bg-app text-app border-faint border rounded-t-3xl sm:rounded-3xl p-6 sm:p-7 shadow-[0_-12px_48px_rgba(0,0,0,0.55)]"
                    data-testid="sports-gate-sheet"
                >
                    <div className="flex items-start justify-between mb-5">
                        <div>
                            <p className="eyebrow text-amber-200/85 mb-2">
                                you've shared the game
                            </p>
                            <h2 className="font-serif font-light italic text-app text-2xl leading-tight tracking-tight">
                                keep the rivalry going.
                            </h2>
                        </div>
                        <button
                            onClick={onClose}
                            data-testid="sports-gate-close"
                            className="p-1 rounded-full text-app-3 hover:text-app hover:surface-faint transition-colors"
                        >
                            <X className="w-4 h-4" strokeWidth={1.6} />
                        </button>
                    </div>

                    <p className="font-serif italic text-app-2 text-[14px] leading-snug mb-5">
                        you can still read every card we generate — but to keep <em>sharing</em> them, grab a pass.
                    </p>

                    {/* GAME PASS — primary */}
                    <button
                        onClick={() => buyPass("game")}
                        disabled={busy !== null}
                        data-testid="sports-gate-game-pass"
                        className="w-full text-left rounded-2xl border-2 border-amber-300/60 bg-amber-300/[0.08] px-5 py-4 mb-3 hover:bg-amber-300/[0.12] transition-all disabled:opacity-50"
                    >
                        <div className="flex items-baseline justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                                <Ticket className="w-4 h-4 text-amber-200" strokeWidth={1.6} />
                                <span className="eyebrow text-amber-100 font-semibold">Game Pass</span>
                            </div>
                            <div className="font-serif text-amber-100 text-xl tracking-tight">$4.99</div>
                        </div>
                        <p className="font-serif italic text-app text-[13px] leading-snug">
                            unlimited shares — for the entire live duration of tonight's game.
                        </p>
                    </button>

                    {/* SERIES PASS — value upsell */}
                    {seriesAvailable && (
                        <button
                            onClick={() => buyPass("series")}
                            disabled={busy !== null}
                            data-testid="sports-gate-series-pass"
                            className="w-full text-left rounded-2xl border-subtle border surface-subtle px-5 py-4 hover:surface-strong transition-colors disabled:opacity-50"
                        >
                            <div className="flex items-baseline justify-between mb-1.5">
                                <div className="flex items-center gap-2">
                                    <Trophy className="w-4 h-4 text-app-2" strokeWidth={1.6} />
                                    <span className="eyebrow text-app">Series Pass</span>
                                    <span className="font-serif italic text-[10px] text-amber-200/85">— best value</span>
                                </div>
                                <div className="font-serif text-app text-xl tracking-tight">$14.99</div>
                            </div>
                            <p className="font-serif italic text-app-2 text-[13px] leading-snug">
                                covers every game of this series. one tap, all the way to the finish.
                            </p>
                        </button>
                    )}

                    {requiresLogin && (
                        <div className="mt-4 flex items-center gap-2 rounded-full bg-amber-300/[0.08] border border-amber-300/25 px-3.5 py-2">
                            <Sparkles className="w-3 h-3 text-amber-200" strokeWidth={1.6} />
                            <p className="font-sans text-[11px] text-amber-100">
                                sign in to attach the pass to your account
                            </p>
                        </div>
                    )}

                    <p className="mt-5 text-center font-serif italic text-app-3 text-[11px]">
                        no subscriptions. one-time, game-day only.
                    </p>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
